import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import {
  CreateCultureData,
  CreateVarietyData,
  GetCultureProductsRequestData,
  GetSeedEntriesList,
  UpdateCultureData,
  UpdateVarietyData,
} from 'api/product';
import {
  PaginationData,
  Culture,
  Product,
  SeedInventoryEntry,
  Variety,
} from 'constants/interface';
import { RootState } from 'state';

const cultureEntityAdapter = createEntityAdapter<Culture>();
const cultureProductsEntityAdapter = createEntityAdapter<Product>();
const varietyEntityAdapter = createEntityAdapter<Variety>();
const productEntityAdapter = createEntityAdapter<Product>();
const entriesEntityAdapter = createEntityAdapter<SeedInventoryEntry>();

export interface CultureProductsState extends EntityState<Product> {
  isLoading: boolean
  remoteTotal: number
}

export interface CulturesState extends EntityState<Culture> {
  isLoading: boolean
  remoteTotal: number
  products: Record<EntityId, CultureProductsState>
}

export interface VarietiesState extends EntityState<Variety> {
  isLoading: boolean
  remoteTotal: number
}

export interface SeedEntriesState extends EntityState<SeedInventoryEntry> {
  isLoading: boolean
  remoteTotal: number
}

export interface ProductsState extends EntityState<Product> {
  isLoading: boolean
  remoteTotal: number
  cultures: CulturesState
  varieties: VarietiesState
  entries: SeedEntriesState
}

export const initialState: ProductsState = productEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
  cultures: cultureEntityAdapter.getInitialState({
    isLoading: false,
    remoteTotal: 0,
    products: {},
  }),
  varieties: varietyEntityAdapter.getInitialState({
    isLoading: false,
    remoteTotal: 0,
  }),
  entries: entriesEntityAdapter.getInitialState({
    isLoading: false,
    remoteTotal: 0,
  }),
});

// Culture
export const createCultureAction = createAsyncThunk(
  'cultures/create',
  async (payload: CreateCultureData) => {
    const cultureData = await apiClient.product.createCulture(payload);
    return cultureData;
  },
);

export const updateCultureAction = createAsyncThunk(
  'cultures/update',
  async (payload: UpdateCultureData) => {
    const cultureData = await apiClient.product.updateCulture(payload);
    return cultureData;
  },
);

export const getCultureListAction = createAsyncThunk(
  'cultures/list',
  async (payload: PaginationData) => {
    const response = await apiClient.product.getCultureList(payload);

    return response;
  },
);

// export const setVarietiesAction = createAction<Variety[]>(
//   'products/setVarietiesAction',
// );

export const getCultureProductsAction = createAsyncThunk(
  'cultures/products',
  async (payload: GetCultureProductsRequestData) => {
    const response = await apiClient
      .product
      .getCultureProducts(payload);

    return response;
  },
);

// Variety
export const getVarietyListAction = createAsyncThunk(
  'varieties/list',
  async (payload: PaginationData) => {
    const response = await apiClient.product.getVarietyList(payload);

    return response;
  },
);

export const createVarietyAction = createAsyncThunk(
  'varieties/create',
  async (payload: CreateVarietyData) => {
    const varietyData = await apiClient
      .product
      .createVariety(payload);
    return varietyData;
  },
);

export const updateVarietyAction = createAsyncThunk(
  'varieties/update',
  async (payload: UpdateVarietyData) => {
    const varietyData = await apiClient
      .product
      .updateVariety(payload);
    return varietyData;
  },
);

export const getVarietyProductsAction = createAsyncThunk(
  'varieties/products',
  async (payload: {
    varietyId: string
  }) => {
    const response = await apiClient
      .product
      .getVarietyProducts(payload.varietyId);

    return response;
  },
);

// Product
export const getProductListAction = createAsyncThunk(
  'products/list',
  async (payload: PaginationData) => {
    const response = await apiClient
      .product
      .getProductList(payload);

    return response;
  },
);

// Entries
export const getSeedEntriesListAction = createAsyncThunk(
  'seeds/entries/list',
  async (payload: GetSeedEntriesList) => {
    const response = await apiClient
      .product
      .getSeedEntriesList(payload);

    return response;
  },
);

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    cleanProductsAction: productEntityAdapter.removeAll,
    setProductsAction: productEntityAdapter.setMany,
    cleanCulturesAction: (state) => {
      cultureEntityAdapter.removeAll(state.cultures);
    },
    setCulturesAction: (state, action: PayloadAction<Culture[]>) => {
      cultureEntityAdapter.setMany(state.cultures, action.payload);
    },
    cleanVarietiesAction: (state) => {
      varietyEntityAdapter.removeAll(state.varieties);
    },
    setVarietiesAction: (state, action: PayloadAction<Variety[]>) => {
      varietyEntityAdapter.addMany(state.varieties, action.payload);
    },
    cleanSeedEntriesAction: (state) => {
      entriesEntityAdapter.removeAll(state.entries);
    },
  },
  extraReducers: (builder) => {
    // Products
    builder.addCase(getProductListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getProductListAction.fulfilled, (state, action) => {
      productEntityAdapter.removeAll(state);
      productEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getProductListAction.rejected, (state) => {
      state.isLoading = false;
    });

    // Cultures
    builder.addCase(getCultureListAction.pending, (state) => {
      state.cultures.isLoading = true;
    });
    builder.addCase(getCultureListAction.fulfilled, (state, action) => {
      cultureEntityAdapter.removeAll(state.cultures);
      cultureEntityAdapter.addMany(state.cultures, action.payload.data);
      state.cultures.remoteTotal = action.payload.pagination.total;
      state.cultures.isLoading = false;
    });
    builder.addCase(getCultureListAction.rejected, (state) => {
      state.cultures.isLoading = false;
    });

    // builder.addCase(getCultureProductsAction.pending, (state, action) => {
    //   const { cultureId } = action.meta.arg;
    //   if (state.cultures.products[cultureId]) {
    //     state.cultures.products[cultureId].isLoading = true;
    //   } else {
    //     state.cultures.products[cultureId] = cultureProductsEntityAdapter.getInitialState({
    //       isLoading: true,
    //       remoteTotal: 0,
    //     });
    //   }
    // });
    // builder.addCase(getCultureProductsAction.fulfilled, (state, action) => {
    //   const { cultureId } = action.meta.arg;
    //   if (state.cultures.products[cultureId]) {
    //     cultureEntityAdapter.removeAll(state.cultures.products[cultureId]);
    //     cultureEntityAdapter.addMany(state.cultures.products[cultureId], action.payload.data);
    //     state.cultures.products[cultureId].remoteTotal = action.payload.pagination.total;
    //     state.cultures.products[cultureId].isLoading = false;
    //   }
    // });
    // builder.addCase(getCultureProductsAction.rejected, (state, action) => {
    //   const { cultureId } = action.meta.arg;
    //   if (state.cultures.products[cultureId]) {
    //     state.cultures.products[cultureId].isLoading = false;
    //   }
    // });
    builder.addCase(getCultureProductsAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getCultureProductsAction.fulfilled, (state, action) => {
      productEntityAdapter.setMany(state, action.payload.data);
      state.isLoading = false;
    });
    builder.addCase(getCultureProductsAction.rejected, (state) => {
      state.isLoading = false;
    });

    // Varieties
    builder.addCase(getVarietyListAction.pending, (state) => {
      state.varieties.isLoading = true;
    });
    builder.addCase(getVarietyListAction.fulfilled, (state, action) => {
      cultureEntityAdapter.removeAll(state.varieties);
      cultureEntityAdapter.addMany(state.varieties, action.payload.data);
      state.varieties.remoteTotal = action.payload.pagination.total;
      state.varieties.isLoading = false;
    });
    builder.addCase(getVarietyListAction.rejected, (state) => {
      state.varieties.isLoading = false;
    });

    // builder.addCase(setVarietiesAction, (state, action) => {
    //   varietyEntityAdapter.addMany(state.varieties, action.payload);
    // });

    // Entries
    builder.addCase(getSeedEntriesListAction.pending, (state) => {
      state.entries.isLoading = true;
    });
    builder.addCase(getSeedEntriesListAction.fulfilled, (state, action) => {
      entriesEntityAdapter.removeAll(state.entries);
      entriesEntityAdapter.addMany(state.entries, action.payload.data);
      state.entries.remoteTotal = action.payload.pagination.total;
      state.entries.isLoading = false;
    });
    builder.addCase(getSeedEntriesListAction.rejected, (state) => {
      state.entries.isLoading = false;
    });
  },
});

export const {
  cleanProductsAction,
  setProductsAction,
  cleanCulturesAction,
  setCulturesAction,
  cleanVarietiesAction,
  // setVarietiesAction,
  cleanSeedEntriesAction,
} = productsSlice.actions;

// Products

export const isLoadingProductsSelector = (state: RootState):
  boolean => state.products.isLoading;

export const productsEntitiesSelector = (state: RootState):
  Dictionary<Product> => state.products.entities;

export const productsIdsSelector = (state: RootState):
  EntityId[] => state.products.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.products.remoteTotal;

// export const cultureProducts = (state: RootState, cultureId: string):
//   Product[] => {
//     const cultureProductsState = state.ge
//     return [];
//   }
// export const cultureProductsSelector = (state: RootState, cultureId: string):
//   Product[] | undefined => {
//   const cultureProductsState = state.products.cultures.products[cultureId];
//   if (cultureProductsState) {
//     return cultureProductsState
//       .ids
//       .map((productId) => {
//         const productEntity = cultureProductsState.entities[productId];
//         return productEntity;
//       })
//       .filter((product) => !!product) as Product[];
//   }

//   return undefined;
// };
export const cultureProductsSelector = (state: RootState):
  Record<EntityId, CultureProductsState> => state.products.cultures.products;

// Cultures

export const isLoadingCulturesSelector = (state: RootState):
  boolean => state.products.cultures.isLoading;

export const culturesEntitiesSelector = (state: RootState):
  Dictionary<Culture> => state.products.cultures.entities;

export const culturesIdsSelector = (state: RootState):
  EntityId[] => state.products.cultures.ids;

export const culturesRemoteTotalSelector = (state: RootState):
  number => state.products.cultures.remoteTotal;

// Varieties

export const isLoadingVarietiesSelector = (state: RootState):
  boolean => state.products.varieties.isLoading;

export const varietiesEntitiesSelector = (state: RootState):
  Dictionary<Variety> => state.products.varieties.entities;

export const varietiesIdsSelector = (state: RootState):
  EntityId[] => state.products.varieties.ids;

export const varietiesRemoteTotalSelector = (state: RootState):
  number => state.products.varieties.remoteTotal;

// Entries selectors

export const isLoadingSeedEntriesSelector = (state: RootState):
  boolean => state.trays.entries.isLoading;

export const seedEntriesEntitiesSelector = (state: RootState):
  Dictionary<SeedInventoryEntry> => state.products.entries.entities;

export const seedEntriesIdsSelector = (state: RootState):
  EntityId[] => state.products.entries.ids;

export const seedEntriesRemoteTotalSelector = (state: RootState):
  number => state.products.entries.remoteTotal;
